import { Text } from '@chakra-ui/react';
import { Warning } from '@phosphor-icons/react';
import React, { useEffect } from 'react';

import { GlobalReducerState } from 'app/reducers';
import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { useProfileContext } from 'Contexts/profileContext';
import { useTextContext } from 'Contexts/textContext';
import { Icon, Tooltip } from 'DesignLibrary/atoms';
import { PageLayout } from 'DesignLibrary/atoms/PageLayout';
import { useViewport } from 'DesignLibrary/context/viewportContext';
import { COLORS } from 'DesignLibrary/vars';
import { ASK_ALEX_PATH, getEnvironmentFromUrl, MTM_PATH, RESULT_PATH, WELCOME_PATH } from 'Utils/urls';

import Desktop from './Desktop';
import { ReviewModeText, ReviewModeWrapper } from './Desktop/styled';
import Mobile from './Mobile';
import { getPages } from './pages';
import { DEPLOY_ENVIRONMENTS } from '../../../constants';

export const determineRoutes = (
  isMobile: boolean,
  isFullBenefitEnabled: boolean,
  showHsaNav: boolean,
  showTaxSavings: boolean,
  showResultPage: boolean,
  hasUnsavedProfileChanges: boolean,
) => {
  // Need to fix this behavior @dan_warrick
  // Causes nav bar to break
  if (hasUnsavedProfileChanges) {
    //   return [];
  }
  if (isMobile) {
    return getPages(isFullBenefitEnabled, showHsaNav, showTaxSavings, showResultPage);
  }
  return getPages(isFullBenefitEnabled, showHsaNav, showTaxSavings, showResultPage);
};

export interface NavigationProps {
  path: string;
  isFullBenefitEnabled: boolean;
  showHsaNav: boolean;
  showTaxSavings: boolean;
  showResultPage: boolean;
  canChangePayPeriod: boolean;
  unlockedPath: string;
  isAlexIdEnabled: boolean;
  hasUnsavedProfileChanges: boolean;
  hideNavItems: boolean;
  handleUnlockPath: (path: string) => void;
  getStore: () => GlobalReducerState;
}

export const Navigation = ({
  path,
  isFullBenefitEnabled,
  showHsaNav,
  showTaxSavings,
  showResultPage,
  canChangePayPeriod,
  unlockedPath,
  isAlexIdEnabled,
  hasUnsavedProfileChanges,
  hideNavItems,
  getStore,
  handleUnlockPath,
}: NavigationProps) => {
  const { device } = useViewport();
  const { is_profile_review_tool_enabled } = useFeatureFlagContext();
  const { reviewMode } = useProfileContext();
  const { retrieveContentfulData } = useTextContext();

  const pages = determineRoutes(
    device !== 'desktop',
    isFullBenefitEnabled,
    showHsaNav,
    showTaxSavings,
    showResultPage,
    hasUnsavedProfileChanges,
  );
  const currentPage = pages.find((p) => p.paths.includes(path));
  const unlockedPage = pages.find((p) => p.paths.includes(unlockedPath));

  const reviewModeHeader = retrieveContentfulData<string>('top_level_navigation.review_mode_header');

  // Hide navigation completely on Ask Alex page
  if (path === ASK_ALEX_PATH) {
    return null;
  }

  useEffect(() => {
    if (path !== '/' && currentPage) {
      if (unlockedPage && currentPage.idx > unlockedPage.idx) {
        handleUnlockPath(path);
      }
    }
  }, [path]);

  const loginWarning = isAlexIdEnabled &&
    getEnvironmentFromUrl() === DEPLOY_ENVIRONMENTS.UAT &&
    path !== WELCOME_PATH && (
      <div className="login-banner">
        <PageLayout bg="--primary-blue">
          <Text align="right">
            <Warning size={16} weight="fill" /> For the best review experience, do not sign in.{' '}
            <Tooltip
              label="ALEX will save your answers across sessions. Testing multiple
              scenarios while signed in may result in conflicting information.
              If you log in and something looks amiss, log out and try again."
              desktopOverrides={{
                bg: COLORS.blue[500],
                color: '#000',
                placement: 'bottom-end',
                size: 'large',
              }}
            >
              <strong>Why?</strong>
            </Tooltip>
          </Text>
        </PageLayout>
      </div>
    );

  const showReviewModeBanner =
    path === RESULT_PATH && is_profile_review_tool_enabled && reviewMode ? (
      <ReviewModeWrapper>
        <Icon type="Eye" color="--colors-gray-0" />
        <ReviewModeText>{reviewModeHeader}</ReviewModeText>
      </ReviewModeWrapper>
    ) : null;

  return device === 'desktop' || path === WELCOME_PATH ? (
    <>
      {showReviewModeBanner}
      <Desktop
        pages={pages}
        getStore={getStore}
        unlockedPage={unlockedPage}
        path={path}
        canChangePayPeriod={canChangePayPeriod}
        isAlexIdEnabled={isAlexIdEnabled}
        hideNavItems={hideNavItems}
        hideNavButtons={path === MTM_PATH} // Hide login & signup buttons if path is MTM
      />
      {loginWarning}
    </>
  ) : (
    <>
      {showReviewModeBanner}
      <Mobile
        getStore={getStore}
        pages={pages}
        unlockedPage={unlockedPage}
        path={path}
        canChangePayPeriod={canChangePayPeriod}
        currentPage={currentPage}
        isAlexIdEnabled={isAlexIdEnabled}
        hideNavItems={hideNavItems}
      />
      {loginWarning}
    </>
  );
};
